import { useEffect, useRef } from 'react'
import useSWR from 'swr'
import useSWRInfinite from 'swr/infinite'
import {
  SocialTopicCountsQuery,
  SocialTopicCountsResponse,
  SocialTopicPostItem,
  SocialUniverseItem,
  SocialUniversePostsResponse,
  SocialUniverseRunItem,
} from '@digital-twins/shared-types'
import { useSocialListeningApi } from './SocialListeningApiContext'
import { isSocialUniverseBusy, SOCIAL_UNIVERSES_POLL_MS } from './useSocialUniverses'

export const SOCIAL_UNIVERSE_SWR_KEY = 'social-listening/universe'
export const SOCIAL_UNIVERSE_RUNS_SWR_KEY = 'social-listening/universe-runs'
export const SOCIAL_UNIVERSE_COUNTS_SWR_KEY = 'social-listening/universe-counts'
export const SOCIAL_UNIVERSE_HIDDEN_SWR_KEY = 'social-listening/universe-hidden'

/** True for an SWR key of one Topic's counts (any group, filter or refresh key). */
export function isSocialUniverseCountsKey(key: unknown, token: string): boolean {
  return Array.isArray(key) && key[0] === SOCIAL_UNIVERSE_COUNTS_SWR_KEY && key[1] === token
}

export function getSocialUniverseRefreshInterval(latest: SocialUniverseItem | undefined): number {
  return latest && isSocialUniverseBusy(latest) ? SOCIAL_UNIVERSES_POLL_MS : 0
}

/** Stable SWR key part for a counts query (filters in a fixed order). */
export function socialCountsQueryKey(query: SocialTopicCountsQuery): string {
  const filters = query.filters ?? {}
  return JSON.stringify([
    query.groupBy,
    query.includeMarket ?? false,
    query.window?.from ?? null,
    query.window?.to ?? null,
    filters.themeId ?? null,
    filters.brandId ?? null,
    filters.platform ?? null,
    filters.sentiment ?? null,
    filters.source ?? null,
    filters.window?.from ?? null,
    filters.window?.to ?? null,
    filters.q ?? null,
    filters.author?.platform ?? null,
    filters.author?.handle ?? null,
    filters.market ?? null,
  ])
}

interface UseSocialUniverseResult {
  universe: SocialUniverseItem | null
  isLoading: boolean
  hasError: boolean
  /** True while a channel is collecting or new posts are being sorted; the hook polls meanwhile. */
  isBusy: boolean
  refresh: () => Promise<SocialUniverseItem | undefined>
  /** Puts a mutation response in the cache without a refetch. */
  replace: (universe: SocialUniverseItem) => void
}

/**
 * One Topic for its drawer (`?universe=<token>`). `fallback` is the item from
 * the Topic list, so the drawer renders at once; the hook still fetches the
 * fresh item and polls while the Topic is busy.
 */
export function useSocialUniverse(
  token: string | null,
  fallback: SocialUniverseItem | null = null,
): UseSocialUniverseResult {
  const socialListeningApi = useSocialListeningApi()
  const { data, error, isLoading, mutate } = useSWR<SocialUniverseItem>(
    token ? [SOCIAL_UNIVERSE_SWR_KEY, token] : null,
    () => socialListeningApi.getUniverse(token as string),
    {
      revalidateOnFocus: false,
      fallbackData: fallback && fallback.token === token ? fallback : undefined,
      refreshInterval: getSocialUniverseRefreshInterval,
    },
  )
  const universe = data ?? null

  return {
    universe,
    isLoading: Boolean(token) && isLoading && !data,
    hasError: Boolean(error),
    isBusy: universe ? isSocialUniverseBusy(universe) : false,
    refresh: () => mutate(),
    replace: (next) => {
      void mutate(next, { revalidate: false })
    },
  }
}

interface UseSocialUniverseRunsResult {
  runs: Array<SocialUniverseRunItem>
  isLoading: boolean
  hasError: boolean
  refresh: () => Promise<unknown>
}

/**
 * A Topic's Activity: runs across its channels, newest first. Polls while
 * `isBusy`, and refetches once when it turns false, so the newest run never
 * stays on "Sorting posts…" because the last poll landed just before it closed.
 */
export function useSocialUniverseRuns(
  token: string | null,
  isBusy = false,
): UseSocialUniverseRunsResult {
  const socialListeningApi = useSocialListeningApi()
  const { data, error, isLoading, mutate } = useSWR(
    token ? [SOCIAL_UNIVERSE_RUNS_SWR_KEY, token] : null,
    () => socialListeningApi.listUniverseRuns(token as string),
    {
      revalidateOnFocus: false,
      refreshInterval: isBusy ? SOCIAL_UNIVERSES_POLL_MS : 0,
    },
  )
  const wasBusyRef = useRef(isBusy)

  useEffect(() => {
    if (wasBusyRef.current && !isBusy && token) {
      void mutate()
    }
    wasBusyRef.current = isBusy
  }, [isBusy, token, mutate])

  return {
    runs: data?.runs ?? [],
    isLoading: Boolean(token) && isLoading,
    hasError: Boolean(error),
    refresh: () => mutate(),
  }
}

/** Hidden posts per page of the Hidden section. */
export const SOCIAL_HIDDEN_POSTS_LIMIT = 50

interface UseSocialUniverseHiddenPostsResult {
  posts: Array<SocialTopicPostItem>
  /**
   * False when the server cannot list hidden posts (it refused the request,
   * or answered with posts that are not hidden): the caller shows the posts
   * hidden in this session instead.
   */
  isSupported: boolean
  /** True while the server has more hidden posts than `posts` lists. */
  hasMore: boolean
  isLoadingMore: boolean
  loadMore: () => void
}

/**
 * The posts a manager hid from a Topic, for the drawer's Hidden section and
 * its Unhide, a page at a time (cursor paging). `refreshKey` refetches from
 * the first page after a hide or unhide. Null `token` (no Topic, or hiding
 * not offered) makes no request.
 */
export function useSocialUniverseHiddenPosts(
  token: string | null,
  refreshKey: string | number | null = null,
): UseSocialUniverseHiddenPostsResult {
  const socialListeningApi = useSocialListeningApi()
  const { data, error, size, setSize, isValidating } = useSWRInfinite<SocialUniversePostsResponse>(
    (pageIndex: number, previous: SocialUniversePostsResponse | null) => {
      if (!token) {
        return null
      }
      if (pageIndex > 0 && !previous?.nextCursor) {
        return null
      }
      return [SOCIAL_UNIVERSE_HIDDEN_SWR_KEY, token, refreshKey, previous?.nextCursor ?? null]
    },
    (key: [string, string, string | number | null, string | null]) => {
      const cursor = key[3]
      return socialListeningApi.listHiddenUniversePosts(
        key[1],
        cursor ? { limit: SOCIAL_HIDDEN_POSTS_LIMIT, cursor } : { limit: SOCIAL_HIDDEN_POSTS_LIMIT },
      )
    },
    {
      revalidateOnFocus: false,
      revalidateFirstPage: false,
      keepPreviousData: true,
      shouldRetryOnError: false,
    },
  )
  const pages = data ?? []
  const posts = pages.flatMap((page) => page.posts)
  // A server before `hidden=only` refuses the unknown param (error). Guard the
  // other way too: a listed post that is not hidden means the param was dropped.
  const isSupported = !error && posts.every((post) => post.hidden === true)
  const lastPage = pages.length > 0 ? pages[pages.length - 1] : null
  const hasMore = isSupported && Boolean(lastPage?.nextCursor)
  const isLoadingMore = isValidating && size > pages.length
  return {
    posts: isSupported ? posts : [],
    isSupported,
    hasMore,
    isLoadingMore,
    loadMore: () => {
      if (hasMore && !isLoadingMore) {
        void setSize(size + 1)
      }
    },
  }
}

interface UseSocialUniverseCountsResult {
  counts: SocialTopicCountsResponse | null
  isLoading: boolean
  hasError: boolean
}

/**
 * Post counts of a Topic grouped by theme, brand, sentiment, channel or week,
 * for the filter chips and the sentiment line. `refreshKey` (for example the
 * Topic's `lastSyncedAt` plus its busy flag) refetches when new posts land.
 */
export function useSocialUniverseCounts(
  token: string | null,
  query: SocialTopicCountsQuery,
  refreshKey: string | number | null = null,
): UseSocialUniverseCountsResult {
  const socialListeningApi = useSocialListeningApi()
  const { data, error, isLoading } = useSWR(
    token
      ? [SOCIAL_UNIVERSE_COUNTS_SWR_KEY, token, socialCountsQueryKey(query), refreshKey]
      : null,
    () => socialListeningApi.countUniversePosts(token as string, query),
    { revalidateOnFocus: false, keepPreviousData: true },
  )
  return {
    counts: data ?? null,
    isLoading: Boolean(token) && isLoading,
    hasError: Boolean(error),
  }
}
